import { Filter, Interval } from '@elderbyte/ngx-starter'
import { Instant } from '@js-joda/core'
import { OrderCompletionState } from './work-order'
import { OrderConfirmationState } from '../prod-orders/prod-order'

export class WorkOrderFilterBuilder {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public static readonly MANUFACTURE_FACILITY_KEY = 'manufactureFacility'
  public static readonly COLLECTING_FACILITY_KEY = 'collectingFacility'
  public static readonly PROD_ORDER_IDS_KEY = 'prodOrderIds'
  public static readonly SCHEDULED_INTERVAL_KEY = 'scheduleInterval'
  public static readonly SCHEDULED_INTERVAL_BEFORE_KEY = 'scheduledIntervalBefore'
  public static readonly ALLOW_EMPTY_KEY = 'allowEmpty'
  public static readonly COMPLETION_STATES_KEY = 'completionStates'
  public static readonly CONFIRMATION_STATES_KEY = 'orderConfirmationState'
  public static readonly MISSING_PRODUCTION_ROUTES_KEY = 'missingProductionRoutes'

  private _manufactureFacility: string
  private _collectingFacility: string
  private _prodOrders: string[]
  private _scheduledInterval: Interval
  private _scheduledIntervalBefore: Instant
  private _allowEmpty = true
  private _completionStates: OrderCompletionState[]
  private _confirmationStates: OrderConfirmationState[]
  private _missingProductionRoutes: boolean

  /***************************************************************************
   *                                                                         *
   * Static Builders                                                         *
   *                                                                         *
   **************************************************************************/

  public static start(): WorkOrderFilterBuilder {
    return new WorkOrderFilterBuilder()
  }

  public static manufactureFacility(facility: string): Filter {
    return this.start().manufactureFacility(facility).build()[0]
  }

  public static collectingFacility(collectingFacility: string): Filter {
    return this.start().collectingFacility(collectingFacility).build()[0]
  }

  public static prodOrders(...prodOrders: string[]): Filter {
    return this.start()
      .prodOrders(...prodOrders)
      .build()[0]
  }

  public static scheduleInterval(interval: Interval): Filter {
    return this.start().scheduledInterval(interval).build()[0]
  }

  public static scheduleIntervalBefore(instant: Instant): Filter {
    return this.start().scheduledIntervalBefore(instant).build()[0]
  }

  public static allowEmpty(allowEmpty: boolean): Filter {
    return this.start().allowEmpty(allowEmpty).build()[0]
  }

  public static completionStates(...states: OrderCompletionState[]): Filter {
    return this.start()
      .completionStates(...states)
      .build()[0]
  }

  public static confirmationStates(...states: OrderConfirmationState[]): Filter {
    return this.start()
      .confirmationStates(...states)
      .build()[0]
  }

  public static missingProductionRoutes(missingProductionRoutes: boolean): Filter {
    return this.start().missingProductionRoutes(missingProductionRoutes).build()[0]
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public manufactureFacility(facility: string): WorkOrderFilterBuilder {
    this._manufactureFacility = facility
    return this
  }

  public collectingFacility(collectingFacility: string): WorkOrderFilterBuilder {
    this._collectingFacility = collectingFacility
    return this
  }

  public prodOrders(...prodOrders: string[]): WorkOrderFilterBuilder {
    this._prodOrders = prodOrders
    return this
  }

  public scheduledInterval(interval: Interval): WorkOrderFilterBuilder {
    this._scheduledInterval = interval
    return this
  }

  public scheduledIntervalBefore(instant: Instant): WorkOrderFilterBuilder {
    this._scheduledIntervalBefore = instant
    return this
  }

  public allowEmpty(allowEmpty: boolean): WorkOrderFilterBuilder {
    this._allowEmpty = allowEmpty
    return this
  }

  public completionStates(...states: OrderCompletionState[]): WorkOrderFilterBuilder {
    this._completionStates = states
    return this
  }

  public confirmationStates(...states: OrderConfirmationState[]): WorkOrderFilterBuilder {
    this._confirmationStates = states
    return this
  }

  public missingProductionRoutes(missingProductionRoutes: boolean): WorkOrderFilterBuilder {
    this._missingProductionRoutes = missingProductionRoutes
    return this
  }

  public build(): Filter[] {
    const filters: Filter[] = []

    if (this._manufactureFacility) {
      filters.push(
        new Filter(WorkOrderFilterBuilder.MANUFACTURE_FACILITY_KEY, this._manufactureFacility)
      )
    }

    if (this._collectingFacility) {
      filters.push(
        new Filter(WorkOrderFilterBuilder.COLLECTING_FACILITY_KEY, this._collectingFacility)
      )
    }

    if (this._prodOrders) {
      filters.push(new Filter(WorkOrderFilterBuilder.PROD_ORDER_IDS_KEY, this._prodOrders))
    }

    if (this._scheduledInterval) {
      filters.push(
        new Filter(
          WorkOrderFilterBuilder.SCHEDULED_INTERVAL_KEY,
          this._scheduledInterval.toInstantString()
        )
      )
    }

    if (this._scheduledIntervalBefore) {
      filters.push(
        new Filter(
          WorkOrderFilterBuilder.SCHEDULED_INTERVAL_BEFORE_KEY,
          this._scheduledIntervalBefore.toString()
        )
      )

      if (this._missingProductionRoutes) {
        filters.push(
          new Filter(
            WorkOrderFilterBuilder.MISSING_PRODUCTION_ROUTES_KEY,
            String(this._missingProductionRoutes)
          )
        )
      }
    }

    filters.push(new Filter(WorkOrderFilterBuilder.ALLOW_EMPTY_KEY, '' + this._allowEmpty)) // TODO hack

    if (this._completionStates) {
      filters.push(new Filter(WorkOrderFilterBuilder.COMPLETION_STATES_KEY, this._completionStates))
    }

    if (this._confirmationStates) {
      filters.push(
        new Filter(WorkOrderFilterBuilder.CONFIRMATION_STATES_KEY, this._confirmationStates)
      )
    }

    return filters
  }
}
